import { useQuery } from '@tanstack/react-query';
import { BoloResource, EventResource } from '@mark43/cad-api';
import { useApiClient } from '@m43/core';
import { AttachmentsPanelType } from '../types';

export type EntityNumberState =
    | { status: 'loading' }
    | { status: 'settled'; value: string | undefined };

interface UseAttachmentEntityNumberProps {
    entityId: number | undefined;
    entityType: AttachmentsPanelType | undefined;
    entityNumberFromPanelState: string | undefined;
}

export function useAttachmentEntityNumber({
    entityId,
    entityType,
    entityNumberFromPanelState,
}: UseAttachmentEntityNumberProps): EntityNumberState {
    const apiClient = useApiClient();
    const shouldFetch = !entityNumberFromPanelState && !!entityId;

    const eventQuery = useQuery({
        queryKey: ['attachment-entity-number', 'event', entityId],
        queryFn() {
            return apiClient
                .request<EventResource.GetAgencyEventById>({
                    method: 'GET',
                    url: `event/agency/${entityId}/`,
                })
                .then((res) => res.data.data?.agencyEventNumber);
        },
        enabled: shouldFetch && entityType === 'CAD_AGENCY_EVENT',
        // 3 total attempts (1 initial + 2 retries); on exhaustion falls back to #unknown via undefined value
        retry: 2,
    });

    const boloQuery = useQuery({
        queryKey: ['attachment-entity-number', 'bolo', entityId],
        queryFn() {
            return apiClient
                .request<BoloResource.GetBoloPanel>({
                    method: 'POST',
                    url: 'bolo/panel',
                    data: { boloId: entityId },
                })
                .then((res) => res.data.data?.boloPanelView.boloPanelMetadata.boloNumber);
        },
        enabled: shouldFetch && entityType === 'BOLO',
        // 3 total attempts (1 initial + 2 retries); on exhaustion falls back to #unknown via undefined value
        retry: 2,
    });

    if (entityNumberFromPanelState) {
        return { status: 'settled', value: entityNumberFromPanelState };
    }

    if (!entityId || !entityType) {
        return { status: 'settled', value: undefined };
    }

    const activeQuery = entityType === 'CAD_AGENCY_EVENT' ? eventQuery : boloQuery;

    if (activeQuery.isSuccess || activeQuery.isError) {
        return { status: 'settled', value: activeQuery.data ?? undefined };
    }

    return { status: 'loading' };
}
